import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { GungNotificationService } from 'gung-common';
import { first } from 'rxjs';
import { Order } from '../../../../models/order';
import { MetadataService } from '../../../../services/metadata/metadata.service';
import { ZendrService } from '../../services/zendr.service';

@Component({
  selector: 'lib-production-management-create-zendr-shipment-modal',
  templateUrl: './production-management-create-zendr-shipment-modal.component.html',
  styleUrls: ['./production-management-create-zendr-shipment-modal.component.css'],
  standalone: false
})
export class ProductionManagementCreateZendrShipmentModalComponent implements OnInit {
  @Input() order: Order;
  protected zendrPackageTypeTable: string = 'ZendrPackageTypes';

  public activeModal = inject(NgbActiveModal);
  protected formBuilder = inject(FormBuilder);
  protected zendrService = inject(ZendrService);
  protected gungNotificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);
  protected metadataService = inject(MetadataService);

  form: FormGroup;
  submitting = false;
  hasPackageTypeMetadata = false;

  ngOnInit(): void {
    const metaOptions = this.metadataService.getMetadataTableList(this.zendrPackageTypeTable);
    this.hasPackageTypeMetadata = !!metaOptions && metaOptions.length > 0;

    this.form = this.formBuilder.group({
      packages: this.formBuilder.array([this.createPackageGroup()])
    });
  }

  get packages(): FormArray {
    return this.form.get('packages') as FormArray;
  }

  createPackageGroup(): FormGroup {
    return this.formBuilder.group({
      type: this.formBuilder.control('package', [Validators.required]),
      width: this.formBuilder.control(null, [Validators.required, Validators.min(0)]),
      height: this.formBuilder.control(null, [Validators.required, Validators.min(0)]),
      length: this.formBuilder.control(null, [Validators.required, Validators.min(0)]),
      count: this.formBuilder.control(1, [Validators.required, Validators.min(1)]),
      weight: this.formBuilder.control(null, [Validators.required, Validators.min(0)]),
      stackable: this.formBuilder.control(true),
      description: this.formBuilder.control('')
    });
  }

  addPackage(): void {
    this.packages.push(this.createPackageGroup());
  }

  removePackage(index: number): void {
    this.packages.removeAt(index);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const payload = {
      orderId: this.order.id,
      packages: this.packages.getRawValue()
    };

    this.zendrService
      .createShipment(payload)
      .pipe(first())
      .subscribe({
        next: result => {
          this.submitting = false;
          this.gungNotificationService.notify(
            this.translateService.instant('ZENDR_SHIPMENT'),
            this.translateService.instant('ZENDR_SHIPMENT_CREATED'),
            'success'
          );
          this.activeModal.close(result);
        },
        error: err => {
          this.submitting = false;
          const message = err?.error?.message || err?.error?.gungMessage || this.translateService.instant('ZENDR_SHIPMENT_ERROR');
          this.gungNotificationService.notify(this.translateService.instant('ERROR'), message, 'error');
        }
      });
  }
}
